/**
 * MathText Component - Renders text with inline LaTeX math
 *
 * Uses KaTeX directly for lightweight, fast rendering.
 * LaTeX expressions are wrapped in $...$ delimiters.
 *
 * Example: "Simplify $\frac{4}{7} \times \frac{2}{3}$"
 */
import { useMemo } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

interface MathTextProps {
  /** Text with optional $...$ LaTeX delimiters */
  children: string | undefined | null;
  /** Display as block (centered) or inline */
  block?: boolean;
  /** Additional CSS class */
  className?: string;
}

/**
 * Renders text with inline math notation using KaTeX
 */
export function MathText({ children, block = false, className = '' }: MathTextProps) {
  const rendered = useMemo(() => {
    if (!children) return '';
    const text = String(children);

    // Split on $...$ patterns, keeping delimiters
    const parts = text.split(/(\$[^$]+\$)/g);

    return parts.map((part, i) => {
      if (part.startsWith('$') && part.endsWith('$')) {
        // Extract LaTeX and render
        const latex = part.slice(1, -1);
        try {
          const html = katex.renderToString(latex, {
            throwOnError: false,
            displayMode: block,
          });
          return `<span class="katex-inline" key="${i}">${html}</span>`;
        } catch (e) {
          console.error('KaTeX error:', e);
          return `<span class="katex-error">${latex}</span>`;
        }
      }
      // Escape HTML in regular text
      return part.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }).join('');
  }, [children, block]);

  return (
    <span
      className={`math-text ${className}`}
      dangerouslySetInnerHTML={{ __html: rendered }}
    />
  );
}

/**
 * Renders pure LaTeX (no text parsing)
 */
export function Math({ latex, block = false, className = '' }: {
  latex: string;
  block?: boolean;
  className?: string;
}) {
  const html = useMemo(() => {
    try {
      return katex.renderToString(latex, {
        throwOnError: false,
        displayMode: block,
      });
    } catch (e) {
      console.error('KaTeX error:', e);
      return `<span class="katex-error">${latex}</span>`;
    }
  }, [latex, block]);

  const Tag = block ? 'div' : 'span';
  return (
    <Tag
      className={`math ${className}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/**
 * Common math notation helpers for Claude to generate
 */
export const MathHelpers = {
  fraction: (num: number | string, den: number | string) =>
    `\\frac{${num}}{${den}}`,

  mixedNumber: (whole: number, num: number, den: number) =>
    `${whole}\\frac{${num}}{${den}}`,

  multiply: (...terms: string[]) => terms.join(' \\times '),

  divide: (a: string, b: string) => `${a} \\div ${b}`,

  power: (base: string, exp: string | number) => `${base}^{${exp}}`,

  sqrt: (expr: string) => `\\sqrt{${expr}}`,

  withUnit: (value: string | number, unit: string) =>
    `${value}\\text{ ${unit}}`,
};

export default MathText;
